import { useCallback } from 'react';
import type { NarrativeForm } from '../../utils/schema';
import type {
  FuturePEMultipleCalculationInput,
  FuturePEMultipleCalculationOutput,
  FuturePEMultipleFairValueCalculationInput,
  FuturePEMultipleFairValueCalculationOutput,
} from '../Calculators/useFuturePEMultipleCalculation';

export const useNarrativeFormHandler = ({
  values,
  calculateFairValue,
  calculateRevenueGrowthRateFromFairValue,
  setPartialValues,
}: {
  values: Partial<NarrativeForm>;
  calculateFairValue: (
    args: FuturePEMultipleCalculationInput
  ) => FuturePEMultipleCalculationOutput;
  calculateRevenueGrowthRateFromFairValue: (
    args: FuturePEMultipleFairValueCalculationInput
  ) => FuturePEMultipleFairValueCalculationOutput;
  setPartialValues: (values: Partial<NarrativeForm>) => void;
}) => {
  const handleSetPartialValues = useCallback(
    (values: Partial<NarrativeForm>) => {
      const safeValuesHandledByNarrativeForm = {
        discountRatePercentage: values.discountRatePercentage || 0,
        futurePE: values.priceEarningsMultiple || 0,
        profitMarginPercentage: values.profitMarginPercentage || 0,
        revenueGrowthRatePercentage: values.revenuePercentage || 0,
        timeFrame: values.timePeriod || 5,
      };

      const calculatorOutputs = calculateFairValue({
        ...safeValuesHandledByNarrativeForm,
      });

      setPartialValues({
        ...values,
        ...calculatorOutputs,
      });
    },
    [calculateFairValue, setPartialValues]
  );

  const handleSetFairValue = useCallback(
    (fairValue: number) => {
      const safeValuesHandledByNarrativeForm = {
        discountRatePercentage: values.discountRatePercentage || 0,
        futurePE: values.priceEarningsMultiple || 0,
        profitMarginPercentage: values.profitMarginPercentage || 0,
        timeFrame: values.timePeriod || 5,
      };

      const { revenueGrowthRatePercentage } =
        calculateRevenueGrowthRateFromFairValue({
          fairValue,
          ...safeValuesHandledByNarrativeForm,
        });

      setPartialValues({
        ...values,
        revenuePercentage: revenueGrowthRatePercentage,
        fairValue: fairValue,
      });
    },
    [calculateRevenueGrowthRateFromFairValue, setPartialValues, values]
  );

  const handleContentChange = useCallback(
    (content: { text: string; html: string }) => {
      setPartialValues({
        ...values,
        content,
      });
    },
    [setPartialValues, values]
  );

  const handleHeaderChange = useCallback(
    (newValues: Partial<NarrativeForm>) => {
      setPartialValues({
        ...values,
        ...newValues,
      });
    },
    [setPartialValues, values]
  );

  return {
    handleSetPartialValues,
    handleSetFairValue,
    handleContentChange,
    handleHeaderChange,
  };
};
